import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL, FREE_TIER_FEATURES } from "@/lib/subscriptionAccess";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { BubblePopGame } from "@/components/BubblePopGame";
import { WeeklyStreakReward } from "@/components/WeeklyStreakReward";
import { fireConfetti } from "@/lib/confetti";
import ShopFooter from "@/components/ShopFooter";
import audioManager from "@/lib/audioManager";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sun,
  Check,
  X,
  Compass,
  Target,
  Sparkles,
  Flame,
  RefreshCw,
  AlertCircle,
  HelpCircle,
  Shield,
  Zap,
  Calendar,
  Trophy,
  Lock,
  ExternalLink,
  Crown,
  BookOpen,
  Users,
  Music,
  Wind,
  Archive,
  ChevronRight,
  ChevronDown,
  Award,
  Brain,
  Clock,
  Cloud,
  Waves,
  ArrowLeft,
  Play,
  Pause,
  Volume2
} from "lucide-react";
import { BADGE_DEFINITIONS } from "@/components/Badges";

// Check-in obstacle reasons
const OBSTACLE_REASONS = [
  { id: "avoidance", label: "Avoidance", icon: Shield, color: "text-red-400", bgColor: "bg-red-400/20" },
  { id: "uncertainty", label: "Uncertainty", icon: HelpCircle, color: "text-amber-400", bgColor: "bg-amber-400/20" },
  { id: "fear", label: "Fear", icon: AlertCircle, color: "text-orange-400", bgColor: "bg-orange-400/20" },
  { id: "distraction", label: "Distraction", icon: Zap, color: "text-blue-400", bgColor: "bg-blue-400/20" },
];

// Feature categories with tier info - Go Deeper section (removed free tier)
const FEATURE_CATEGORIES = {
  monthly: [
    { id: "aicoach", label: "PathFinder", icon: Flame, path: "/pathfinder" },
    { id: "community", label: "Community", icon: Users, path: "/community" },
    { id: "tools", label: "Tools & Vault", icon: Archive, path: "/discover?tab=digital_vault" },
    { id: "fullmeditations", label: "All Meditations", icon: Wind, path: "/discover?tab=audio_vault" },
    { id: "fullsoundscapes", label: "All Soundscapes", icon: Music, path: "/discover?tab=audio_vault" },
  ],
  quarterly: [
    { id: "badges", label: "Achievement Badges", icon: Award, path: "/profile" },
    { id: "reflections", label: "Weekly Reflections", icon: BookOpen, path: "/reflections" },
    { id: "aicoach", label: "PathFinder", icon: Flame, path: "/pathfinder" },
  ],
  annual: [
    { id: "quests", label: "Transformation Quests", icon: Target, path: "/discover?tab=digital_vault" },
    { id: "pathfinder", label: "Pathfinder Prize", icon: Trophy, path: null, expandable: true },
  ],
};

// Daily Compass tools (formerly Quick Tools) - available to all users
const DAILY_COMPASS_TOOLS = [
  { id: "mood", label: "Mood Tracker", icon: Sparkles, path: "/mood-tracker", color: "from-purple-500/20 to-pink-500/20" },
  { id: "energy", label: "Energy Check-In", icon: Zap, path: "/energy-check-in", color: "from-yellow-500/20 to-orange-500/20" },
  { id: "wellness", label: "Wellness Check", icon: Brain, path: "/wellness-check", color: "from-blue-500/20 to-cyan-500/20" },
  { id: "breathe", label: "Breathing Exercise", icon: Wind, path: "/breathe", color: "from-green-500/20 to-emerald-500/20" },
  { id: "reframe", label: "Thought Reframe", icon: RefreshCw, path: "/thought-reframe", color: "from-indigo-500/20 to-purple-500/20" },
  { id: "focus", label: "Focus Timer", icon: Clock, path: "/focus-timer", color: "from-rose-500/20 to-red-500/20" },
];

export default function CompassPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { subscription } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [signalTimeout, setSignalTimeout] = useState(false);
  
  // Today's data
  const [todaysSignal, setTodaysSignal] = useState(null);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [showReasonSelector, setShowReasonSelector] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReasonText, setOtherReasonText] = useState("");
  const [savingAction, setSavingAction] = useState(false);
  const [savingCheckIn, setSavingCheckIn] = useState(false);
  
  // Progress data
  const [streak, setStreak] = useState(0);
  const [miniQuestProgress, setMiniQuestProgress] = useState(null);
  const [newestBadge, setNewestBadge] = useState(null);
  const [badgeProgress, setBadgeProgress] = useState(null);
  const [completedWeeks, setCompletedWeeks] = useState(0);
  const [showStreakReward, setShowStreakReward] = useState(true);
  const [pathfinderPrizeExpanded, setPathfinderPrizeExpanded] = useState(false);
  
  // Trial status for downgrade card
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissingCard, setDismissingCard] = useState(false);
  
  // Soundscape playback state
  const [playingSoundscape, setPlayingSoundscape] = useState(null);
  
  // Signal loading timeout - show fallback after 8 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!todaysSignal && loading) {
        setSignalTimeout(true);
        setLoading(false);
      }
    }, 8000);
    return () => clearTimeout(timeout);
  }, [todaysSignal, loading]);
  
  // Fetch trial status
  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const response = await axios.get(`${API}/trial/status`, { withCredentials: true });
        setTrialStatus(response.data);
      } catch (err) {
        // Trial status is optional - fail silently but log in dev
        if (process.env.NODE_ENV === 'development') {
          console.debug('Trial status fetch failed:', err.message);
        }
      }
    };
    fetchTrialStatus();
  }, []);
  
  // Dismiss trial downgrade card
  const dismissDowngradeCard = async () => {
    setDismissingCard(true);
    try {
      await axios.post(`${API}/trial/dismiss-downgrade-card`, {}, { withCredentials: true });
      setTrialStatus(prev => ({ ...prev, downgrade_card_dismissed: true }));
    } catch (err) {
      toast.error("Failed to dismiss card");
    } finally {
      setDismissingCard(false);
    }
  };
  
  // Free soundscape audio URLs
  const FREE_SOUNDSCAPES = {
    rain: {
      id: "rain",
      title: "Rain",
      audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Light%20Gentle%20Rain%20Part%201.mp3"
    },
    ocean: {
      id: "ocean", 
      title: "Ocean Waves",
      audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Birds%20With%20Ocean%20Waves%20on%20the%20Beach.mp3"
    }
  };
  
  // Handle soundscape play/pause
  const handleSoundscapeToggle = (soundscapeId) => {
    const soundscape = FREE_SOUNDSCAPES[soundscapeId];
    if (!soundscape) return;
    
    if (playingSoundscape === soundscapeId) {
      // Stop playing
      audioManager.stop();
      setPlayingSoundscape(null);
    } else {
      // Start playing new soundscape
      audioManager.play(soundscape.audioUrl, true); // true = loop
      setPlayingSoundscape(soundscapeId);
      toast.success(`Now playing: ${soundscape.title}`);
    }
  };
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (playingSoundscape) {
        audioManager.stop();
      }
    };
  }, [playingSoundscape]);

  // Admin users bypass all subscription gating
  const isAdmin = user?.role === 'admin';
  const isFreeUser = subscription.type === 'none' && !isAdmin;
  const canAccessMiniQuests = hasAccess(subscription.type, 'monthly', user);
  const canAccessBadges = hasAccess(subscription.type, 'quarterly', user);

  const fetchCompassData = useCallback(async () => {
    try {
      const [dashRes, journalRes, gamificationRes, miniQuestRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { withCredentials: true }),
        axios.get(`${API}/journal?tag=compass`, { withCredentials: true }),
        axios.get(`${API}/gamification/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/mini-quest/current`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);

      // Set today's signal
      setTodaysSignal(dashRes.data.todays_signal);
      setStreak(dashRes.data.streak || 0);

      // Check today's entries
      const today = new Date().toISOString().split('T')[0];
      const allCompassEntries = journalRes.data || [];
      const todayEntries = allCompassEntries.filter(e => e.created_at?.startsWith(today));
      
      setActionCompleted(!!todayEntries.find(e => e.tags?.includes('compass-action')));
      const checkInEntry = todayEntries.find(e => e.tags?.includes('compass-checkin'));
      if (checkInEntry) {
        setCheckInStatus(checkInEntry.tags?.includes('checkin-yes') ? 'yes' : 'no');
      }

      // Set mini-quest progress
      if (miniQuestRes.data) {
        setMiniQuestProgress(miniQuestRes.data);
      }

      // Process gamification data
      if (gamificationRes.data) {
        // Get completed weeks count
        setCompletedWeeks(gamificationRes.data.mini_quest_completed_weeks || 0);
        
        // Find newest earned badge
        const earnedBadges = gamificationRes.data.badges?.filter(b => b.earned) || [];
        if (earnedBadges.length > 0) {
          const newest = earnedBadges.sort((a, b) => 
            new Date(b.earned_at || 0) - new Date(a.earned_at || 0)
          )[0];
          setNewestBadge(newest);
        }

        // Find badge closest to earning
        const unearnedBadges = gamificationRes.data.badges?.filter(b => !b.earned && b.progress > 0) || [];
        if (unearnedBadges.length > 0) {
          const closest = unearnedBadges.sort((a, b) => b.progress - a.progress)[0];
          setBadgeProgress(closest);
        }
      }

    } catch (error) {
      console.error("Failed to fetch compass data:", error);
      // Only show error toast for genuine failures, not initial auth issues
      if (error.response?.status !== 401) {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCompassData();
    }
  }, [token, fetchCompassData]);

  const handleActionComplete = async () => {
    if (savingAction || actionCompleted) return;
    setSavingAction(true);
    
    try {
      await axios.post(`${API}/journal`, {
        title: "Daily Action",
        body: `Completed today's action: ${todaysSignal?.prompt || 'Daily action'}`,
        tags: ["compass", "compass-action"],
        source: "compass"
      }, { withCredentials: true });
      
      setActionCompleted(true);
      toast.success("Action marked complete!");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSavingAction(false);
    }
  };

  const handleCheckIn = async (followed) => {
    if (savingCheckIn) return;
    
    if (!followed) {
      setShowReasonSelector(true);
      return;
    }
    
    setSavingCheckIn(true);
    try {
      await axios.post(`${API}/journal`, {
        title: "Daily Check-in",
        body: "Followed through on today's intention",
        tags: ["compass", "compass-checkin", "checkin-yes"],
        source: "compass"
      }, { withCredentials: true });
      
      setCheckInStatus('yes');
      toast.success("Great job following through!");
      // Fire confetti for successful check-in
      fireConfetti('checkin');
    } catch (error) {
      toast.error("Failed to save check-in");
    } finally {
      setSavingCheckIn(false);
    }
  };

  const submitNoCheckIn = async () => {
    if (!selectedReason) {
      toast.error("Please select a reason");
      return;
    }
    
    setSavingCheckIn(true);
    try {
      const reasonLabel = selectedReason === "other" 
        ? otherReasonText || "Other reason"
        : OBSTACLE_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
      
      await axios.post(`${API}/journal`, {
        title: "Daily Check-in",
        body: `Did not follow through. Reason: ${reasonLabel}`,
        tags: ["compass", "compass-checkin", "checkin-no", selectedReason],
        source: "compass"
      }, { withCredentials: true });
      
      setCheckInStatus('no');
      setShowReasonSelector(false);
      setSelectedReason(null);
      setOtherReasonText("");
      toast.success("Check-in recorded. Tomorrow is a new day.");
    } catch (error) {
      toast.error("Failed to save check-in");
    } finally {
      setSavingCheckIn(false);
    }
  };

  // Render feature link or locked state
  const renderFeatureItem = (feature, isLocked = false) => {
    const Icon = feature.icon;
    
    if (isLocked) {
      return (
        <div 
          key={feature.id}
          className="flex items-center gap-3 p-2 rounded-lg bg-white/5 opacity-60"
        >
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <Lock className="w-4 h-4 text-white/40" />
          </div>
          <span className="text-white/40 text-sm flex-1">{feature.label}</span>
        </div>
      );
    }
    
    if (feature.path) {
      return (
        <button
          key={feature.id}
          onClick={() => navigate(feature.path)}
          className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors w-full text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-[#a69373]/20 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#a69373]" />
          </div>
          <span className="text-white text-sm flex-1">{feature.label}</span>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </button>
      );
    }
    
    // Expandable Pathfinder Prize accordion
    if (feature.expandable && feature.id === 'pathfinder') {
      return (
        <div key={feature.id} className="rounded-lg bg-white/5 overflow-hidden">
          <button
            onClick={() => setPathfinderPrizeExpanded(!pathfinderPrizeExpanded)}
            className="flex items-center gap-3 p-2 w-full text-left hover:bg-white/10 transition-colors"
          >
            <div className="w-8 h-8 rounded-lg bg-[#a69373]/20 flex items-center justify-center">
              <Icon className="w-4 h-4 text-[#a69373]" />
            </div>
            <span className="text-white text-sm flex-1">{feature.label}</span>
            <ChevronDown 
              className={`w-4 h-4 text-white/40 transition-transform ${pathfinderPrizeExpanded ? 'rotate-180' : ''}`} 
            />
          </button>
          {pathfinderPrizeExpanded && (
            <div className="px-4 pb-4 pt-2 border-t border-white/10">
              <h4 className="text-[#a69373] font-medium text-sm mb-2">The Pathfinder Prize</h4>
              <p className="text-white/70 text-xs leading-relaxed">
                Each quarter, one Annual member who has completed a Transformation Quest and demonstrated meaningful growth is recognised as a Pathfinder. Winners receive a curated prize bundle selected by the TrueNorth Zone team. Details of each prize are announced inside the app and via email.
              </p>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div 
        key={feature.id}
        className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
      >
        <div className="w-8 h-8 rounded-lg bg-[#a69373]/20 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#a69373]" />
        </div>
        <span className="text-white text-sm">{feature.label}</span>
      </div>
    );
  };

  // Don't block rendering - show content immediately with loading states for data
  // Only show full-page loading if we don't even have auth context yet
  if (!token && loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#a69373] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="compass-page">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
        style={{
          backgroundImage: `linear-gradient(rgba(11,40,33,0.9), rgba(11,40,33,0.95)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#a69373]" />
            </div>
            <div>
              <h1 className="text-lg font-heading text-[#a69373]">Compass</h1>
              <p className="text-xs text-[#a69373]/60">Day {todaysSignal?.day_number || 1}</p>
            </div>
          </div>
          {streak > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#a69373]/20">
              <Flame className="w-4 h-4 text-[#a69373]" />
              <span className="text-[#a69373] text-sm font-semibold">{streak}</span>
            </div>
          )}
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Weekly Streak Reward - shows at 7-day milestones */}
        {showStreakReward && (
          <WeeklyStreakReward 
            streak={streak} 
            onDismiss={() => setShowStreakReward(false)} 
          />
        )}

        {/* Trial Downgrade Card - one-time after trial expires */}
        {trialStatus?.trial_expired && !trialStatus?.downgrade_card_dismissed && (
          <Card className="glass-card border-[#a69373]/30 animate-fade-in">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-1">Your trial has ended</h3>
                  <p className="text-white/60 text-sm mb-3">
                    You're now on the free-forever tier — you keep your account, journals, and basic tools.
                    Upgrade to get everything back.
                  </p>
                  <div className="flex gap-2">
                    <a
                      href="https://www.truenorth-zone.com/pages/compass-membership"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#a69373] hover:bg-[#b89a5e] rounded-lg text-sm font-semibold transition-colors"
                      style={{ color: '#0B2821' }}
                      data-testid="trial-upgrade-btn"
                    >
                      <Crown className="w-4 h-4" style={{ color: '#0B2821' }} />
                      Upgrade
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={dismissDowngradeCard}
                      disabled={dismissingCard}
                      className="text-white/50 hover:text-white/80"
                    >
                      {dismissingCard ? 'Dismissing...' : 'Dismiss'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Anchor Word Card - shows if user has anchor from companion trial */}
        {trialStatus?.anchor_word && trialStatus?.is_active && (
          <Card className="glass-card border-[#a69373]/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#a69373]/20 flex items-center justify-center">
                  <Compass className="w-5 h-5 text-[#a69373]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs">Your North Star This Week</p>
                  <p className="text-[#a69373] font-heading font-bold text-lg capitalize">
                    {trialStatus.anchor_word}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 2x2 Grid - responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Box 1: Today */}
          <Card className="glass-card animate-stagger-fade stagger-1 card-hover-lift" data-testid="today-box">
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4 flex items-center gap-2" style={{ color: '#a69373' }}>
                <Sun className="w-5 h-5" />
                Today
              </h2>
              
              <div className="space-y-4">
                {/* Today's Signal */}
                <div className="p-3 rounded-xl bg-white/5">
                  <p className="text-white/50 text-xs font-medium mb-1">Today's Signal</p>
                  <p className="text-white/80 text-sm">
                    {todaysSignal?.prompt 
                      ? todaysSignal.prompt 
                      : signalTimeout 
                        ? "Check back shortly for today's signal" 
                        : "Loading..."}
                  </p>
                </div>

                {/* Today's Action */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex-1 mr-3">
                    <p className="text-white/50 text-xs font-medium">Today's Action</p>
                    <p className="text-white text-sm">{todaysSignal?.action_text || "Complete today's signal"}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleActionComplete}
                    disabled={actionCompleted || savingAction}
                    className={actionCompleted 
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821]"
                    }
                    data-testid="action-complete-btn"
                  >
                    {actionCompleted ? <Check className="w-4 h-4" /> : "Done"}
                  </Button>
                </div>

                {/* Check-in Section */}
                {checkInStatus === null && !showReasonSelector && (
                  <div className="p-4 rounded-xl bg-[#a69373]/10 border border-[#a69373]/20">
                    <p className="text-[#a69373] text-sm font-medium mb-3">Did you follow through today?</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleCheckIn(true)}
                        disabled={savingCheckIn}
                        className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
                        data-testid="checkin-yes-btn"
                      >
                        <Check className="w-4 h-4 mr-1" /> Yes
                      </Button>
                      <Button
                        onClick={() => handleCheckIn(false)}
                        disabled={savingCheckIn}
                        className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                        data-testid="checkin-no-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> No
                      </Button>
                    </div>
                  </div>
                )}

                {/* Reason Selector */}
                {showReasonSelector && (
                  <div className="p-4 rounded-xl bg-white/5 space-y-3">
                    <p className="text-white/70 text-sm">What got in your way?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {OBSTACLE_REASONS.map((reason) => {
                        const Icon = reason.icon;
                        return (
                          <button
                            key={reason.id}
                            onClick={() => setSelectedReason(reason.id)}
                            className={`p-3 rounded-lg text-left transition-all ${
                              selectedReason === reason.id
                                ? `${reason.bgColor} border border-current ${reason.color}`
                                : "bg-white/5 hover:bg-white/10 text-white/60"
                            }`}
                          >
                            <Icon className="w-4 h-4 mb-1" />
                            <span className="text-xs">{reason.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedReason === "other" && (
                      <Textarea
                        value={otherReasonText}
                        onChange={(e) => setOtherReasonText(e.target.value)}
                        placeholder="What happened?"
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setShowReasonSelector(false);
                          setSelectedReason(null);
                        }}
                        variant="ghost"
                        className="flex-1 text-white/60"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={submitNoCheckIn}
                        disabled={!selectedReason || savingCheckIn}
                        className="flex-1 bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821]"
                      >
                        Submit
                      </Button>
                    </div>
                  </div>
                )}

                {/* Check-in complete state */}
                {checkInStatus && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 ${
                    checkInStatus === 'yes' 
                      ? 'bg-green-500/10 border border-green-500/20' 
                      : 'bg-amber-500/10 border border-amber-500/20'
                  }`}>
                    {checkInStatus === 'yes' ? (
                      <>
                        <Check className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 text-sm">Great job today!</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5 text-amber-400" />
                        <span className="text-amber-400 text-sm">Tomorrow is a fresh start</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Box 2: Daily Compass (Free for all users) */}
          <Card className="glass-card animate-stagger-fade stagger-2 card-hover-lift" data-testid="daily-compass-box">
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4 flex items-center gap-2" style={{ color: '#a69373' }}>
                <Compass className="w-5 h-5" />
                Daily Compass
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {DAILY_COMPASS_TOOLS.map(tool => (
                  <button
                    key={tool.id}
                    onClick={() => navigate(tool.path)}
                    className={`p-3 rounded-xl bg-gradient-to-br ${tool.color} border border-white/10 hover:border-white/30 transition-all text-left`}
                    data-testid={`daily-compass-${tool.id}`}
                  >
                    <tool.icon className="w-5 h-5 text-white/70 mb-2" />
                    <p className="text-white text-sm font-medium">{tool.label}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Box 3: Your Progress (includes Weekly Challenge and Badge Progress) */}
          <Card className="glass-card animate-stagger-fade stagger-3 card-hover-lift" data-testid="progress-box">
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4 flex items-center gap-2" style={{ color: '#a69373' }}>
                <Trophy className="w-5 h-5" />
                Your Progress
              </h2>
              
              <div className="space-y-4">
                {/* Day Streak */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Flame className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Day Streak</p>
                      <p className="text-white text-2xl font-bold">{streak}</p>
                    </div>
                  </div>
                  <p className="text-orange-400/60 text-xs">days</p>
                </div>

                {/* Weekly Challenge Progress (from This Week) */}
                {canAccessMiniQuests ? (
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-white/50 text-xs font-medium mb-2">Weekly Challenge</p>
                    {miniQuestProgress ? (
                      <>
                        <p className="text-white font-medium mb-2">
                          Week {miniQuestProgress.current_week || 1}: {miniQuestProgress.quest_title || "Current Quest"}
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#a69373] rounded-full transition-all"
                              style={{ width: `${(miniQuestProgress.days_completed || 0) / 7 * 100}%` }}
                            />
                          </div>
                          <span className="text-[#a69373] text-sm font-medium">
                            {miniQuestProgress.days_completed || 0}/7
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="text-white/60 text-sm">No active challenge</p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-white/5 text-center">
                    <Lock className="w-6 h-6 text-white/30 mx-auto mb-2" />
                    <p className="text-white/50 text-sm mb-2">Weekly Challenges</p>
                    <a
                      href={MEMBERSHIP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#a69373] text-xs hover:underline inline-flex items-center gap-1"
                    >
                      Upgrade to unlock <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Challenges Completed */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#a69373]/20 flex items-center justify-center">
                      <Target className="w-6 h-6 text-[#a69373]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Challenges Completed</p>
                      <p className="text-white text-xl font-bold">{completedWeeks}/52</p>
                    </div>
                  </div>
                </div>

                {/* Badge Progress (from This Week) */}
                {canAccessBadges ? (
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-white/50 text-xs font-medium mb-2">Badge Progress</p>
                    {badgeProgress ? (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#a69373]/20 flex items-center justify-center">
                          <Award className="w-6 h-6 text-[#a69373]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{badgeProgress.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#a69373] rounded-full"
                                style={{ width: `${badgeProgress.progress || 0}%` }}
                              />
                            </div>
                            <span className="text-[#a69373] text-xs">{Math.round(badgeProgress.progress || 0)}%</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-white/60 text-sm">Start earning badges!</p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-white/5 text-center">
                    <Lock className="w-6 h-6 text-white/30 mx-auto mb-2" />
                    <p className="text-white/50 text-sm mb-2">Badge Progress</p>
                    <a
                      href={MEMBERSHIP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#a69373] text-xs hover:underline inline-flex items-center gap-1"
                    >
                      Upgrade to unlock <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Newest Badge */}
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-white/50 text-xs font-medium mb-2">Newest Badge</p>
                  {newestBadge ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#a69373]/20 flex items-center justify-center">
                        <Award className="w-6 h-6 text-[#a69373]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{newestBadge.name}</p>
                        <p className="text-white/50 text-xs">
                          Earned {newestBadge.earned_at ? new Date(newestBadge.earned_at).toLocaleDateString() : 'recently'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-white/60 text-sm">No badges earned yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Box 4: Go Deeper */}
          <Card className="glass-card animate-stagger-fade stagger-4 card-hover-lift" data-testid="go-deeper-box">
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4 flex items-center gap-2" style={{ color: '#a69373' }}>
                <Sparkles className="w-5 h-5" />
                Go Deeper
              </h2>
              
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                {/* Monthly Features */}
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                    {hasAccess(subscription.type, 'monthly', user) ? 'Monthly+' : (
                      <span className="flex items-center gap-1">
                        Monthly+ <Lock className="w-3 h-3" />
                      </span>
                    )}
                  </p>
                  <p className="text-white/30 text-xs mb-2 italic">All Free Tier tools, plus:</p>
                  <div className="space-y-1">
                    {FEATURE_CATEGORIES.monthly.map(f => 
                      renderFeatureItem(f, !hasAccess(subscription.type, 'monthly', user))
                    )}
                  </div>
                </div>

                {/* Quarterly Features */}
                <div className="pt-2 border-t border-white/10">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                    {hasAccess(subscription.type, 'quarterly', user) ? 'Quarterly+' : (
                      <span className="flex items-center gap-1">
                        Quarterly+ <Lock className="w-3 h-3" />
                      </span>
                    )}
                  </p>
                  <p className="text-white/30 text-xs mb-2 italic">All Monthly+ tools, plus:</p>
                  <div className="space-y-1">
                    {FEATURE_CATEGORIES.quarterly.map(f => 
                      renderFeatureItem(f, !hasAccess(subscription.type, 'quarterly', user))
                    )}
                  </div>
                </div>

                {/* Annual Features */}
                <div className="pt-2 border-t border-white/10">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                    {hasAccess(subscription.type, 'annual', user) ? 'Annual' : (
                      <span className="flex items-center gap-1">
                        Annual <Lock className="w-3 h-3" />
                      </span>
                    )}
                  </p>
                  <p className="text-white/30 text-xs mb-2 italic">All Quarterly+ tools, plus:</p>
                  <div className="space-y-1">
                    {FEATURE_CATEGORIES.annual.map(f => 
                      renderFeatureItem(f, !hasAccess(subscription.type, 'annual', user))
                    )}
                  </div>
                </div>

                {/* Upgrade CTA for free users */}
                {isFreeUser && (
                  <div className="pt-3">
                    <a
                      href={MEMBERSHIP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold transition-colors"
                      data-testid="upgrade-cta"
                    >
                      <Crown className="w-5 h-5" />
                      Unlock All Features
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stress Release Section - Two Columns */}
        <div className="mt-6">
          <h2 className="text-lg font-heading font-bold mb-4 flex items-center gap-2 px-1" style={{ color: '#a69373' }}>
            <RefreshCw className="w-5 h-5" />
            Stress Release
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column: Bubble Game */}
            <div>
              <BubblePopGame />
            </div>
            
            {/* Right Column: Free Soundscapes */}
            <Card className="glass-card">
              <CardContent className="p-4">
                <p className="text-white/50 text-xs font-medium mb-3 flex items-center gap-2">
                  <Volume2 className="w-3 h-3" />
                  Free Soundscapes
                </p>
                <div className="space-y-2">
                  {/* Rain Soundscape */}
                  <button
                    onClick={() => handleSoundscapeToggle('rain')}
                    className={`w-full p-3 rounded-xl bg-gradient-to-r from-[#6B8CAE]/20 to-[#6B8CAE]/10 border transition-all text-left flex items-center gap-3 ${
                      playingSoundscape === 'rain' 
                        ? 'border-[#6B8CAE] ring-2 ring-[#6B8CAE]/30' 
                        : 'border-[#6B8CAE]/30 hover:border-[#6B8CAE]/50'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#6B8CAE]/30 flex items-center justify-center">
                      {playingSoundscape === 'rain' ? (
                        <Pause className="w-5 h-5 text-[#6B8CAE]" />
                      ) : (
                        <Cloud className="w-5 h-5 text-[#6B8CAE]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">Rain</p>
                      <p className="text-white/50 text-xs">
                        {playingSoundscape === 'rain' ? 'Now playing...' : 'Let your thoughts dissolve'}
                      </p>
                    </div>
                    {playingSoundscape === 'rain' && (
                      <div className="w-2 h-2 rounded-full bg-[#6B8CAE] animate-pulse" />
                    )}
                  </button>
                  
                  {/* Ocean Waves Soundscape */}
                  <button
                    onClick={() => handleSoundscapeToggle('ocean')}
                    className={`w-full p-3 rounded-xl bg-gradient-to-r from-[#4A90A4]/20 to-[#4A90A4]/10 border transition-all text-left flex items-center gap-3 ${
                      playingSoundscape === 'ocean' 
                        ? 'border-[#4A90A4] ring-2 ring-[#4A90A4]/30' 
                        : 'border-[#4A90A4]/30 hover:border-[#4A90A4]/50'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#4A90A4]/30 flex items-center justify-center">
                      {playingSoundscape === 'ocean' ? (
                        <Pause className="w-5 h-5 text-[#4A90A4]" />
                      ) : (
                        <Waves className="w-5 h-5 text-[#4A90A4]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">Ocean Waves</p>
                      <p className="text-white/50 text-xs">
                        {playingSoundscape === 'ocean' ? 'Now playing...' : 'Breathe with the waves'}
                      </p>
                    </div>
                    {playingSoundscape === 'ocean' && (
                      <div className="w-2 h-2 rounded-full bg-[#4A90A4] animate-pulse" />
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Shop Footer */}
        <ShopFooter />
      </main>

      <BottomNavigation />
    </div>
  );
}
